import { useEffect, useState } from "react";
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "sonner";
import { GripVertical, ImageOff, PackageOpen, Pencil, Plus, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import ProductFormDialog from "@/components/admin/ProductFormDialog";

const STORE_DOTS = { Amazon: "bg-[#FF9900]", Flipkart: "bg-[#2874F0]" };

function ProductThumb({ product }) {
  const [error, setError] = useState(false);
  if (error || !product.image_url) {
    return (
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-zinc-100 text-zinc-300">
        <ImageOff className="h-4 w-4" />
      </div>
    );
  }
  return (
    <img
      src={product.image_url}
      alt=""
      loading="lazy"
      onError={() => setError(true)}
      className="h-12 w-12 shrink-0 rounded-xl border border-black/5 object-cover"
    />
  );
}

function SortableRow({ product, categoryName, onToggle, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: product.id });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      data-testid={`product-row-${product.id}`}
      className={`flex items-center gap-3 rounded-2xl border border-black/5 bg-white p-3 sm:gap-4 sm:p-4 ${isDragging ? "z-10 shadow-xl" : ""}`}
    >
      <button
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
        data-testid={`drag-handle-${product.id}`}
        className="cursor-grab touch-none rounded-lg p-1.5 text-zinc-300 transition-colors hover:bg-zinc-100 hover:text-zinc-500 active:cursor-grabbing"
      >
        <GripVertical className="h-5 w-5" />
      </button>
      <ProductThumb product={product} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{product.title}</p>
        <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs text-zinc-500">
          {product.price && <span className="tabular-nums">{product.price}</span>}
          <span className="inline-flex items-center gap-1">
            <span className={`h-1.5 w-1.5 rounded-full ${STORE_DOTS[product.store] || "bg-zinc-400"}`} />
            {product.store}
          </span>
          <span>{categoryName || "Uncategorized"}</span>
          <span className="tabular-nums">{product.clicks ?? 0} clicks</span>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1.5 sm:gap-3">
        <Switch
          checked={product.is_published}
          onCheckedChange={(v) => onToggle(product, v)}
          aria-label="Toggle published"
          data-testid={`toggle-publish-${product.id}`}
        />
        <button
          onClick={() => onEdit(product)}
          aria-label="Edit product"
          data-testid={`edit-product-${product.id}`}
          className="flex h-9 w-9 items-center justify-center rounded-full text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-black"
        >
          <Pencil className="h-4 w-4" />
        </button>
        <button
          onClick={() => onDelete(product)}
          aria-label="Delete product"
          data-testid={`delete-product-${product.id}`}
          className="flex h-9 w-9 items-center justify-center rounded-full text-zinc-400 transition-colors hover:bg-red-50 hover:text-red-600"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default function Products() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const load = () => {
    Promise.all([api.get("/products"), api.get("/categories")])
      .then(([p, c]) => {
        setProducts(p.data);
        setCategories(c.data);
      })
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const catById = Object.fromEntries(categories.map((c) => [c.id, c.name]));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = products.findIndex((p) => p.id === active.id);
    const newIndex = products.findIndex((p) => p.id === over.id);
    const next = arrayMove(products, oldIndex, newIndex);
    setProducts(next);
    api
      .put("/products/reorder", { ordered_ids: next.map((p) => p.id) })
      .then(() => toast.success("Order updated"))
      .catch((err) => {
        toast.error(formatApiError(err));
        load();
      });
  };

  const handleToggle = (product, value) => {
    setProducts((list) => list.map((p) => (p.id === product.id ? { ...p, is_published: value } : p)));
    api.put(`/products/${product.id}`, { is_published: value }).catch((err) => {
      toast.error(formatApiError(err));
      load();
    });
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/products/${deleteTarget.id}`);
      toast.success(`Deleted "${deleteTarget.title}"`);
      setProducts((list) => list.filter((p) => p.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">Products</h1>
          <p className="mt-1 text-sm text-zinc-500" data-testid="products-count">
            {products.length} {products.length === 1 ? "product" : "products"} &middot; drag to reorder
          </p>
        </div>
        <Button
          onClick={() => {
            setEditing(null);
            setDialogOpen(true);
          }}
          data-testid="add-product-button"
          className="rounded-full bg-black px-5 text-white hover:bg-zinc-800"
        >
          <Plus className="mr-1.5 h-4 w-4" />
          Add product
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3" data-testid="products-loading">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-20 animate-pulse rounded-2xl border border-black/5 bg-white" />
          ))}
        </div>
      ) : products.length === 0 ? (
        <div className="flex flex-col items-center rounded-3xl border border-dashed border-black/10 bg-white px-6 py-20 text-center" data-testid="products-empty-state">
          <PackageOpen className="h-8 w-8 text-zinc-300" />
          <p className="mt-4 font-heading text-lg font-medium">No products yet</p>
          <p className="mt-1 max-w-xs text-sm text-zinc-500">Paste an Amazon or Flipkart link and your first product will be live in seconds.</p>
          <Button
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
            data-testid="add-first-product-button"
            className="mt-6 rounded-full bg-black px-6 text-white hover:bg-zinc-800"
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Add your first product
          </Button>
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={products.map((p) => p.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3" data-testid="products-list">
              {products.map((p) => (
                <SortableRow
                  key={p.id}
                  product={p}
                  categoryName={catById[p.category_id]}
                  onToggle={handleToggle}
                  onEdit={(prod) => {
                    setEditing(prod);
                    setDialogOpen(true);
                  }}
                  onDelete={setDeleteTarget}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <ProductFormDialog open={dialogOpen} onOpenChange={setDialogOpen} product={editing} categories={categories} onSaved={load} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-3xl" data-testid="delete-product-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading tracking-tight">Delete this product?</AlertDialogTitle>
            <AlertDialogDescription>
              &ldquo;{deleteTarget?.title}&rdquo; will be permanently removed from your page. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" data-testid="delete-cancel-button">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              data-testid="delete-confirm-button"
              className="rounded-full bg-red-600 text-white hover:bg-red-700"
            >
              {deleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
